import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAllInstalledIntegrations,
  InstalledIntegration_ALL_GET,
} from "@/api/client/installedintegration";
import { InstalledIntegrationType } from "@/types/installedintegration";
import { useCallback, useMemo } from "react";
import { useWebSocketSubscription } from "@/context/WebSocketProvider";
import { useReconnectRefetch } from "@/hooks/useReconnectRefetch";
import { CloudEventType } from "@/types/cloud-events";

/**
 * Lean, socket-reactive "is a calendar connected?" signal.
 *
 * Shares the `InstalledIntegration_ALL_GET` query cache with the heavier
 * `useIntegrationsPage` but mounts none of its mutations/Paragon machinery and
 * never polls. WebSocket events are the primary update mechanism:
 * - Subscribes to the `calendar` channel and invalidates the shared cache on
 *   `CALENDAR_CONNECTED`, `CALENDAR_DISCONNECTED`, and `CALENDAR_UPDATED`, so
 *   connect/disconnect propagates live with zero polling.
 * - Reconciles on socket reconnect (events are dropped while disconnected).
 */
export const useHasCalendarConnected = () => {
  const queryClient = useQueryClient();

  // Calendar connect, disconnect, and token-invalidation each emit a
  // `com.focal.calendar.*` event on the per-user `calendar` room; invalidate the
  // shared installed-integrations cache so home/onboarding reflect the change
  // over the socket without polling.
  const handleCalendarMessage = useCallback(
    (event: { type: string }) => {
      if (
        event.type === CloudEventType.CALENDAR_CONNECTED ||
        event.type === CloudEventType.CALENDAR_DISCONNECTED ||
        event.type === CloudEventType.CALENDAR_UPDATED
      ) {
        queryClient.invalidateQueries({
          queryKey: [InstalledIntegration_ALL_GET],
          exact: true,
        });
      }
    },
    [queryClient],
  );

  useWebSocketSubscription("calendar", undefined, handleCalendarMessage);

  // Events are dropped while the socket is down; reconcile calendar-connected
  // state with a one-shot refetch on reconnect.
  useReconnectRefetch(() => {
    queryClient.invalidateQueries({
      queryKey: [InstalledIntegration_ALL_GET],
      exact: true,
    });
  });

  const {
    data: integrations,
    isLoading,
    isFetched,
  } = useQuery({
    queryFn: getAllInstalledIntegrations,
    queryKey: [InstalledIntegration_ALL_GET],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const hasCalendar = useMemo(
    () =>
      integrations?.some(
        (integration) =>
          integration.integration === InstalledIntegrationType.GOOGLECALENDAR ||
          integration.integration ===
            InstalledIntegrationType.MICROSOFTCALENDAR,
      ) ?? false,
    [integrations],
  );

  return {
    hasCalendar,
    isLoading,
    isFetched,
    installedIntegrations: integrations,
  };
};
